import { inject, Injectable } from '@angular/core';
import { Location } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';

/** Where the back button goes when the detail page was opened directly and no origin is known. */
export interface BackFallback {
  readonly projectId: string;
  readonly commands: readonly (string | number)[];
  readonly queryParams: Readonly<Record<string, string>>;
}

function pathOf(url: string): string {
  const end = url.search(/[?#]/);
  return end === -1 ? url : url.slice(0, end);
}

/**
 * Remembers which in-app page the user came from, so a detail page can return to it.
 *
 * The overview state (search term, selected group, filters, expanded groups) lives in the
 * overview's URL. Restoring that URL by going back in the browser history therefore restores
 * the whole context, and the in-app back button behaves exactly like the browser's back button.
 * A hard-coded navigation to the overview list would drop all of it.
 *
 * Only path changes count as leaving a page: an overview rewrites its own query parameters
 * (`replaceUrl`) while the user filters, and that must not replace the remembered origin.
 */
@Injectable({ providedIn: 'root' })
export class NavigationHistoryService {
  private readonly router = inject(Router);
  private readonly location = inject(Location);

  private currentUrl: string | null = null;
  private previousUrl: string | null = null;

  constructor() {
    this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(event => this.record(event.urlAfterRedirects));
  }

  /**
   * Returns to the page the user came from, or to `fallback` for a direct call of the detail URL.
   *
   * The origin only counts when it belongs to the same project; anything else, such as the
   * project list, would strand the user outside the project they are working in.
   */
  back(fallback: BackFallback): void {
    if (this.hasOriginIn(fallback.projectId)) {
      this.location.back();
      return;
    }
    void this.router.navigate([...fallback.commands], { queryParams: fallback.queryParams });
  }

  private hasOriginIn(projectId: string): boolean {
    return this.previousUrl?.startsWith(`/projects/${projectId}/`) === true;
  }

  private record(url: string): void {
    if (this.currentUrl !== null && pathOf(url) !== pathOf(this.currentUrl)) {
      this.previousUrl = this.currentUrl;
    }
    this.currentUrl = url;
  }
}
